/**
 * Fetch a single gateway device for a property.
 * Auto-refreshes every 30s (heartbeat cadence).
 */
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import type { GatewayDevice } from '@/types/gateway';

export function useGatewayDevice(propertyId: string | undefined) {
  const query = useQuery({
    queryKey: ['gateway-device', propertyId],
    queryFn: async (): Promise<GatewayDevice | null> => {
      if (!propertyId) return null;
      const { data, error } = await supabase
        .from('gateway_devices')
        .select('*')
        .eq('property_id', propertyId)
        .maybeSingle();
      if (error) throw new Error(error.message);
      return data as GatewayDevice | null;
    },
    enabled: Boolean(propertyId),
    staleTime: 15_000,
    refetchInterval: 30_000,
  });

  return {
    device: query.data ?? null,
    isLoading: query.isLoading,
    error: query.error as Error | null,
    refetch: query.refetch,
    isFetching: query.isFetching,
  };
}
